const createScannerLaunchLoading = ({
  setLoading,
  setStatus,
  onTimeout = () => {},
  setTimer = (callback, delay) => window.setTimeout(callback, delay),
  clearTimer = (timer) => window.clearTimeout(timer),
}) => {
  let pending = false;
  let timeoutTimer = null;

  const finish = () => {
    if (!pending) return false;
    pending = false;
    if (timeoutTimer !== null) clearTimer(timeoutTimer);
    timeoutTimer = null;
    setLoading(false);
    return true;
  };

  return {
    start() {
      if (pending) return false;
      pending = true;
      setLoading(true, 'Loading scanner…');
      timeoutTimer = setTimer(() => {
        if (!finish()) return false;
        setStatus('The scanner is taking longer than expected. Please try again.', 'warning');
        onTimeout();
        return true;
      }, 20000);
      return true;
    },
    opened() {
      if (!finish()) return false;
      setStatus('Complete your foot scan in the popup.');
      return true;
    },
    failed(message) {
      if (!finish()) return false;
      setStatus(message, 'warning');
      return true;
    },
    finish,
    isPending: () => pending,
  };
};

(() => {
  const steps = ['select', 'size', 'about', 'preference', 'feel', 'scan', 'name', 'ready'];
  const DRAFT_TTL = 24 * 60 * 60 * 1000;
  const STOREFRONT_PROFILE_ENDPOINT = 'https://newscan.cloudstride.com/member-api/api/fizo/storefront/fit-profiles';
  const STOREFRONT_USAGE_EVENT_ENDPOINT = 'https://newscan.cloudstride.com/member-api/api/fizo/storefront/profile-usage-events';
  const SIZE_OPTIONS = {
    man: {
      US_M: ['3.5', '4', '4.5', '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '12.5', '13', '13.5', '14'],
      UK: ['3', '3.5', '4', '4.5', '5', '5.5', '6', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '12.5', '13'],
      EUR: ['35.5', '36', '36.5', '37.5', '38', '38.5', '39', '40', '40.5', '41', '42', '42.5', '43', '44', '44.5', '45', '45.5', '46', '47', '47.5', '48', '48.5']
    },
    woman: {
      US_W: ['5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '12.5', '13', '13.5', '14', '14.5', '15', '15.5'],
      UK: ['3', '3.5', '4', '4.5', '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '12.5', '13', '13.5'],
      EUR: ['35.5', '36', '36.5', '37.5', '38', '38.5', '39', '40', '40.5', '41', '42', '42.5', '43', '44', '44.5', '45', '45.5', '46', '47', '47.5', '48', '48.5']
    }
  };

  const hash = (value) => {
    let result = 0;
    for (let index = 0; index < value.length; index += 1) result = ((result << 5) - result + value.charCodeAt(index)) | 0;
    return Math.abs(result).toString(36);
  };

  const loadVolumentalSdk = (root) => {
    if (window.customElements?.get('volumental-measurement')) return Promise.resolve();
    if (window.FizoVolumentalScriptPromise) return window.FizoVolumentalScriptPromise;
    const source = root.dataset.volumentalScript;
    if (!source) return Promise.reject(new Error('The Volumental scanner is not configured.'));
    const absoluteSource = new URL(source, window.location.href).href;
    const existing = [...document.scripts].find((script) => script.src === absoluteSource);
    if (existing) return Promise.resolve();

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = absoluteSource;
      script.async = true;
      if (root.dataset.volumentalClientId) script.dataset.clientId = root.dataset.volumentalClientId;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', () => reject(new Error('The Volumental scanner could not be loaded.')), { once: true });
      document.head.append(script);
    });
    window.FizoVolumentalScriptPromise = promise.catch((error) => {
      window.FizoVolumentalScriptPromise = null;
      throw error;
    });
    return window.FizoVolumentalScriptPromise;
  };

  document.querySelectorAll('[data-fizo-fit-profile]').forEach((root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const context = root.dataset.context || 'pdp';
    const isHomepageModal = root.classList.contains('fizo-fit-profile--homepage-modal');
    const authenticated = root.dataset.authenticated === 'true';
    const customerGid = authenticated ? (root.dataset.customerGid || '') : '';
    let token = root.dataset.resolvedToken || new URLSearchParams(location.search).get(root.dataset.tokenParam) || '';
    document.addEventListener('fizo:order-token-resolved', (event) => {
      if (context === 'order-rebind' && event.detail?.token) token = event.detail.token;
    });
    const drawer = root.querySelector('.fizo-fit-profile__drawer');
    const backdrop = root.querySelector('.fizo-fit-profile__backdrop');
    const originalParent = root.parentNode;
    const originalNextSibling = root.nextSibling;
    const form = root.querySelector('[data-fizo-fit-form]');
    const status = root.querySelector('[data-fizo-fit-status]');
    const sizeStatus = root.querySelector('[data-fizo-fit-size-status]');
    const nameInput = form?.querySelector('[name="name"]');
    const nameError = root.querySelector('[data-fizo-fit-name-error]');
    const loading = root.querySelector('[data-fizo-fit-loading]');
    const loadingLabel = root.querySelector('[data-fizo-fit-loading-label]');
    const setDrawerLoading = (active, label = 'Loading…') => {
      if (!loading) return;
      if (loadingLabel) loadingLabel.textContent = label;
      loading.hidden = !active;
      drawer?.setAttribute('aria-busy', String(active));
    };
    const setInlineStatus = (node, message = '', tone = '') => {
      if (!node) return;
      node.textContent = message;
      node.classList.toggle('is-warning', tone === 'warning');
      node.classList.toggle('is-success', tone === 'success');
      node.setAttribute('role', tone === 'warning' ? 'alert' : 'status');
    };
    const setNameError = (message = '') => {
      setInlineStatus(nameError, message, message ? 'warning' : '');
      if (nameInput) nameInput.setAttribute('aria-invalid', String(Boolean(message)));
    };
    const profilesNode = root.querySelector('[data-fizo-fit-profiles]');
    const resumeButton = root.querySelector('[data-fizo-fit-resume]');
    let variantMap = [];
    try { variantMap = JSON.parse(root.querySelector('[data-fizo-fit-variant-map]')?.textContent || '[]'); } catch { /* Product metadata is optional outside PDP. */ }
    let index = 0;
    let profiles = [];
    let selectedProfile = null;
    const fitBandEnabled = root.dataset.fitBandEnabled === 'true';
    let resolvedFitVariant = null;
    let editingProfileId = '';
    let flowMode = 'create';
    let email = root.dataset.customerEmail || '';
    let draft = null;
    let toastTimer = null;
    let launchScanner = async () => {};
    const handledScanIds = new Set();

    // Match the PDP detail/reviews drawer: escape section stacking contexts so
    // the Fit Profile overlay always renders above the global navigation.
    if (['pdp', 'order-rebind'].includes(context) && root.parentElement !== document.body) document.body.append(root);

    const draftScope = root.dataset.drawerId || context;
    const storageKey = () => `fizo:storefront-fit:v3:draft:${context}:${hash(email)}:${hash(draftScope)}${context === 'order-rebind' ? `:${hash(token)}` : ''}`;
    const readDraft = () => {
      if (!email) return null;
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey()) || 'null');
        if (!saved || !saved.updatedAt || Date.now() - saved.updatedAt > DRAFT_TTL) { localStorage.removeItem(storageKey()); return null; }
        return saved;
      } catch { return null; }
    };
    const currentFormData = () => Object.fromEntries(new FormData(form).entries());
    const normalizeGender = (value) => ['woman', 'women', 'female'].includes(String(value || '').toLowerCase()) ? 'woman' : 'man';
    const normalizeSizeSystem = (value, gender = 'man') => {
      if (value === 'USA') return gender === 'woman' ? 'US_W' : 'US_M';
      if (value === 'EU') return 'EUR';
      return ['US_M', 'US_W', 'UK', 'EUR'].includes(value) ? value : (gender === 'woman' ? 'US_W' : 'US_M');
    };
    const renderSizeOptions = (gender, system, selected = '') => {
      const grid = root.querySelector('[data-fizo-fit-step="size"] .fizo-fit-profile__size-grid');
      if (!grid) return;
      const values = SIZE_OPTIONS[gender]?.[system] || [];
      grid.replaceChildren(...values.map((value) => {
        const label = document.createElement('label');
        const input = document.createElement('input');
        const text = document.createElement('span');
        input.type = 'radio'; input.name = 'manualSize'; input.value = value; input.required = true; input.checked = value === selected;
        text.textContent = value;
        label.append(input, text);
        return label;
      }));
    };
    const syncSizeUI = (gender, system, selected = '') => {
      const normalizedGender = normalizeGender(gender);
      const normalizedSystem = normalizeSizeSystem(system, normalizedGender);
      root.querySelector('[name="gender"]').value = normalizedGender;
      root.querySelector('[name="sizeSystem"]').value = normalizedSystem;
      root.querySelectorAll('[data-fizo-gender]').forEach((button) => button.classList.toggle('is-active', button.dataset.fizoGender === normalizedGender));
      root.querySelectorAll('[data-fizo-size-system]').forEach((button) => {
        const systemValue = button.dataset.fizoSizeSystem === 'US_M' || button.dataset.fizoSizeSystem === 'US_W' ? (normalizedGender === 'woman' ? 'US_W' : 'US_M') : button.dataset.fizoSizeSystem;
        button.dataset.fizoSizeSystem = systemValue;
        if (systemValue === 'US_M') button.textContent = 'US M';
        if (systemValue === 'US_W') button.textContent = 'US W';
        button.classList.toggle('is-active', systemValue === normalizedSystem);
      });
      renderSizeOptions(normalizedGender, normalizedSystem, selected);
    };
    const convertManualSize = (value, fromGender, fromSystem, toGender, toSystem) => {
      if (!value) return '';
      const fromValues = SIZE_OPTIONS[normalizeGender(fromGender)]?.[normalizeSizeSystem(fromSystem, fromGender)] || [];
      const toValues = SIZE_OPTIONS[normalizeGender(toGender)]?.[normalizeSizeSystem(toSystem, toGender)] || [];
      const index = fromValues.indexOf(String(value));
      return index >= 0 && toValues[index] != null ? toValues[index] : value;
    };
    const syncHeightUnitUI = (unit) => {
      const measurement = root.querySelector('[data-fizo-measurement="height"]');
      if (!measurement) return;
      const isMetric = unit === 'cm';
      measurement.querySelector('[data-fizo-height-fields="imperial"]').hidden = isMetric;
      measurement.querySelector('[data-fizo-height-fields="metric"]').hidden = !isMetric;
      measurement.querySelectorAll('[data-fizo-height-fields="imperial"] input').forEach((input) => { input.disabled = isMetric; });
      measurement.querySelectorAll('[data-fizo-height-fields="metric"] input').forEach((input) => { input.disabled = !isMetric; });
      measurement.querySelector('[data-fizo-height-fields="imperial"] .fizo-fit-profile__measurement-input:first-child .fizo-fit-profile__measurement-suffix').textContent = 'ft';
      measurement.querySelector('[data-fizo-height-fields="imperial"] .fizo-fit-profile__measurement-input:nth-child(2) .fizo-fit-profile__measurement-suffix').textContent = 'in';
      measurement.querySelector('[data-fizo-height-fields="metric"] .fizo-fit-profile__measurement-suffix').textContent = 'cm';
    };
    const convertMeasurementUnit = (target, fromUnit, toUnit) => {
      const measurement = root.querySelector(`[data-fizo-measurement="${target}"]`);
      if (!measurement || fromUnit === toUnit) return;
      if (target === 'height') {
        const feetInput = measurement.querySelector('[name="heightFeet"]');
        const inchesInput = measurement.querySelector('[name="heightInches"]');
        const cmInput = measurement.querySelector('[name="heightCm"]');
        if (fromUnit === 'ft' && toUnit === 'cm') {
          const feet = Number(feetInput?.value);
          const inches = Number(inchesInput?.value);
          if (String(feetInput?.value || '').trim() !== '' || String(inchesInput?.value || '').trim() !== '') cmInput.value = String(Math.round(((Number.isFinite(feet) ? feet : 0) * 12 + (Number.isFinite(inches) ? inches : 0)) * 2.54 * 10) / 10);
        } else if (fromUnit === 'cm' && toUnit === 'ft') {
          const cm = Number(cmInput?.value);
          if (Number.isFinite(cm) && cm > 0) {
            const totalInches = cm / 2.54;
            const feet = Math.floor(totalInches / 12);
            const inches = Math.round((totalInches - feet * 12) * 10) / 10;
            feetInput.value = String(feet);
            inchesInput.value = String(inches);
          }
        }
        [feetInput, inchesInput, cmInput].filter(Boolean).forEach((input) => input.dispatchEvent(new Event('input', { bubbles: true })));
        return;
      }
      const input = measurement.querySelector('[name="weight"]');
      const value = Number(input?.value);
      if (!Number.isFinite(value) || value <= 0) return;
      const converted = fromUnit === 'kg' && toUnit === 'lb' ? value * 2.2046226218 : value / 2.2046226218;
      input.value = String(Math.round(converted * 10) / 10);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    };
    const showValidationToast = (message) => {
      if (!drawer) return;
      let toast = drawer.querySelector('[data-fizo-fit-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'fizo-fit-profile__toast';
        toast.dataset.fizoFitToast = 'true';
        toast.setAttribute('role', 'alert');
        drawer.append(toast);
      }
      toast.textContent = message;
      toast.classList.add('is-visible');
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3200);
    };
    const validationMessage = (field) => {
      if (!field) return 'Please complete this step.';
      if (field.name === 'fitProfileEmail') return 'Please enter a valid email address.';
      if (field.name === 'manualSize') return 'Please select your usual shoe size.';
      if (field.name === 'heightFeet' || field.name === 'heightInches' || field.name === 'heightCm') return 'Please enter your height.';
      if (field.name === 'weight') return 'Please enter your weight.';
      if (field.name === 'looseness') return 'Please select your fit preference.';
      if (field.name === 'feel') return 'Please select how you want your shoes to feel.';
      if (field.name === 'name') return 'Please enter a profile name.';
      return field.validationMessage || 'Please complete this step.';
    };
    const hasDraftValues = (values) => Object.entries(values).some(([name, value]) => {
      if (['context', 'email', 'token', 'updatedAt', 'stepIndex', 'gender', 'sizeSystem'].includes(name)) return false;
      return String(value || '').trim() !== '';
    });
    const saveDraft = (extra = {}) => {
      if (flowMode !== 'create' || !email || !form) return;
      const values = { ...(draft || {}), ...currentFormData(), ...extra };
      if (!hasDraftValues(values) && !draft) return;
      draft = { ...values, context, email, token, stepIndex: index, updatedAt: Date.now() };
      try { localStorage.setItem(storageKey(), JSON.stringify(draft)); } catch { /* Ignore unavailable browser storage. */ }
      if (index === 0 && resumeButton) resumeButton.hidden = false;
    };
    const clearDraft = () => { if (email) localStorage.removeItem(storageKey()); draft = null; if (resumeButton) resumeButton.hidden = true; };
    const hydrateDraft = () => {
      if (!draft || !form) return;
      Object.entries(draft).forEach(([name, value]) => {
        const fields = [...form.querySelectorAll(`[name="${CSS.escape(name)}"]`)];
        fields.forEach((field) => {
          if (field.type === 'radio' || field.type === 'checkbox') field.checked = field.value === value || (field.type === 'checkbox' && value === 'on');
          else field.value = value;
        });
      });
      const gender = normalizeGender(draft.gender);
      const system = normalizeSizeSystem(draft.sizeSystem, gender);
      syncSizeUI(gender, system, draft.manualSize || '');
      ['height', 'weight'].forEach((target) => {
        const allowedUnits = target === 'height' ? ['ft', 'cm'] : ['kg', 'lb'];
        const fallbackUnit = target === 'height' ? 'ft' : 'kg';
        const unit = allowedUnits.includes(draft[`${target}Unit`]) ? draft[`${target}Unit`] : fallbackUnit;
        const hiddenUnit = root.querySelector(`[name="${target}Unit"]`);
        if (hiddenUnit) hiddenUnit.value = unit;
        root.querySelectorAll(`[data-fizo-unit-target="${target}"]`).forEach((button) => button.classList.toggle('is-active', button.dataset.fizoMeasurementUnit === unit));
        if (target === 'height') syncHeightUnitUI(unit);
        if (target !== 'height') root.querySelector(`[data-fizo-measurement="${target}"]`)?.querySelectorAll('.fizo-fit-profile__measurement-suffix').forEach((suffix) => { suffix.textContent = unit; });
      });
      renderScanResult(draft);
    };
    const refreshDraftState = () => { draft = readDraft(); if (resumeButton) resumeButton.hidden = !draft || index !== 0; };
    const renderReadyProfile = () => {
      const selected = root.querySelector('[data-fizo-fit-selected]');
      if (!selected || !form) return;
      const values = currentFormData();
      const name = values.name || selectedProfile?.displayName || selectedProfile?.name || 'Fit Profile';
      const icon = root.dataset.profileIcon || '/cdn/shop/assets/fizo-fit-profile-shoe.svg';
      const createdAt = selectedProfile?.createdAt || selectedProfile?.created_at;
      const parsedCreatedAt = createdAt ? new Date(createdAt) : new Date();
      const date = Number.isNaN(parsedCreatedAt.getTime()) ? new Date().toLocaleDateString('en-GB') : parsedCreatedAt.toLocaleDateString('en-GB');
      selected.innerHTML = `<div class="fizo-fit-profile__ready-card"><span class="fizo-fit-profile__ready-icon"><img src="${icon}" alt=""></span><strong>${name}</strong><button type="button" class="fizo-fit-profile__ready-edit" data-fizo-fit-ready-edit>Edit</button><span class="fizo-fit-profile__ready-radio" aria-hidden="true"></span></div><small class="fizo-fit-profile__ready-date">CREATED ${date}</small>`;
    };
    const body = root.querySelector('.fizo-fit-profile__body');
    const progress = root.querySelector('.fizo-fit-profile__progress');
    const progressTitle = root.querySelector('[data-fizo-fit-progress-title]');
    let progressTitleFrame = 0;
    const syncProgressTitle = () => {
      progressTitleFrame = 0;
      const activeTitle = root.querySelector('[data-fizo-fit-step].is-active > h2');
      const canStick = activeTitle && progress && !progress.hidden && getComputedStyle(progress).display !== 'none';
      const isStuck = Boolean(canStick && body.scrollTop > 0 && activeTitle.getBoundingClientRect().top <= progress.getBoundingClientRect().bottom);
      root.classList.toggle('is-step-title-stuck', isStuck);
      if (!progressTitle) return;
      progressTitle.textContent = activeTitle?.textContent?.trim() || '';
      progressTitle.hidden = !isStuck;
    };
    const queueProgressTitleSync = () => {
      if (progressTitleFrame) return;
      progressTitleFrame = window.requestAnimationFrame(syncProgressTitle);
    };
    body?.addEventListener('scroll', queueProgressTitleSync, { passive: true });
    const setStep = (next) => {
      index = Math.max(0, Math.min(steps.length - 1, next));
      root.classList.toggle('is-selection', index === 0);
      root.classList.toggle('is-size-step', steps[index] === 'size');
      root.classList.toggle('is-about-step', steps[index] === 'about');
      root.classList.toggle('is-preference-step', steps[index] === 'preference');
      root.classList.toggle('is-choice-step', steps[index] === 'preference' || steps[index] === 'feel');
      root.classList.toggle('is-scan-step', steps[index] === 'scan');
      root.classList.toggle('is-name-step', steps[index] === 'name');
      root.classList.toggle('is-ready-step', steps[index] === 'ready');
      root.querySelectorAll('[data-fizo-fit-step]').forEach((node) => node.classList.toggle('is-active', node.dataset.fizoFitStep === steps[index]));
      root.querySelector('[data-fizo-fit-step-number]').textContent = String(index === 0 ? 1 : Math.min(index, 6));
      root.querySelector('[data-fizo-fit-back]').hidden = index === 0 || index === 1 || index === 7;
      root.querySelector('[data-fizo-fit-next]').hidden = index === 0 || index >= 6;
      root.querySelector('[data-fizo-fit-save]').hidden = index !== 6;
      const viewAllButton = root.querySelector('[data-fizo-fit-view-all]');
      if (viewAllButton) viewAllButton.hidden = context !== 'homepage' || index !== 7;
      root.querySelector('[data-fizo-fit-add]').hidden = context !== 'pdp' || (index !== 0 && index !== 7) || root.classList.contains('is-email-gate');
      root.querySelector('[data-fizo-fit-rebind]').hidden = context !== 'order-rebind' || (index !== 0 && index !== 7);
      root.querySelector('[data-fizo-fit-create]').hidden = index !== 0;
      if (resumeButton) resumeButton.hidden = !draft || index !== 0;
      if (index === 7) root.querySelector('[data-fizo-fit-step-number]').textContent = '6';
      if (index === 6) renderSummary();
      if (index === 7) renderReadyProfile();
      if (body) body.scrollTop = 0;
      queueProgressTitleSync();
    };
    const endpoint = (name) => {
      const raw = root.dataset[name] || (name === 'profileEndpoint' && context === 'pdp' ? STOREFRONT_PROFILE_ENDPOINT : '');
      if (!raw || context !== 'order-rebind') return raw;
      const url = new URL(raw, location.origin);
      if (token) url.searchParams.set(root.dataset.tokenParam || 'token', token);
      return `${url.pathname}${url.search}`;
    };
    const requestJson = async (url, options) => {
      // Storefront profile API is cross-origin and does not use browser cookies.
      // Sending credentials here makes the browser reject the response unless
      // the API also opts into Access-Control-Allow-Credentials.
      const target = new URL(url, location.href);
      const response = await fetch(url, { ...options, credentials: target.origin === location.origin ? 'include' : 'omit' });
      const text = await response.text();
      let payload = null;
      try { payload = text ? JSON.parse(text) : null; } catch { /* Keep the HTTP status as the useful error. */ }
      if (!response.ok || (typeof payload?.code === 'number' && payload.code >= 400)) throw new Error(payload?.error || payload?.message || payload?.msg || `Request failed (${response.status})`);
      return payload;
    };
    const recordPdpUsage = async (productForm) => {
      const profileId = selectedProfile?.id || selectedProfile?.profileId;
      if (!profileId || !email) return;
      const variantId = String(productForm?.querySelector('[name="id"]')?.value || root.dataset.variantId || '');
      const variant = variantMap.find((item) => String(item.id) === variantId)
        || (String(resolvedFitVariant?.id || '') === variantId ? resolvedFitVariant : {});
      const quantity = Number(productForm?.querySelector('[name="quantity"]')?.value || 1);
      await requestJson(root.dataset.usageEventEndpoint || STOREFRONT_USAGE_EVENT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email,
          profileId,
          authenticated,
          shopifyCustomerGid: customerGid,
          shopDomain: root.dataset.shopDomain || '',
          productId: String(variant.productId || root.dataset.productId || ''),
          variantId,
          sku: String(variant.sku || ''),
          quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 1
        })
      });
    };
    const profileSize = () => selectedProfile?.effectiveRightSizeUsM
      ?? selectedProfile?.effective_right_size_us_m
      ?? selectedProfile?.manualSizeUsM
      ?? selectedProfile?.manual_size_us_m
      ?? selectedProfile?.manualSize
      ?? selectedProfile?.manual_size
      ?? '';
    const normalizedSize = (value) => {
      const number = Number(String(value || '').replace(/^US\s*(M|MEN'?S)?\s*/i, '').trim());
      return Number.isFinite(number) ? String(number) : String(value || '').trim();
    };
    const resolveFitBandForProfile = async (productForm) => {
      if (!fitBandEnabled) return null;
      const fitBandIndex = Number(root.dataset.fitBandOptionPosition || 0) - 1;
      if (fitBandIndex < 0) return null;
      const profileId = selectedProfile?.id || selectedProfile?.profileId;
      const productInfo = productForm?.closest('product-info');
      const colour = productInfo?.querySelector('[data-option-name="Color"]:checked, [data-option-name="Colour"]:checked, [data-option-name="颜色"]:checked')?.value || '';
      const result = await requestJson(root.dataset.fitBandEndpoint, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({
          email,
          profileId,
          shopDomain: root.dataset.shopDomain || '',
          productId: root.dataset.productId || '',
          colour,
          size: profileSize(),
          colourOptionPosition: Number(root.dataset.colourOptionPosition || 0),
          sizeOptionPosition: Number(root.dataset.sizeOptionPosition || 0),
          fitBandOptionPosition: Number(root.dataset.fitBandOptionPosition || 0)
        })
      });
      if (!result?.applicable || !result?.fitBand || !result?.variantId) throw new Error('This product is missing its Fit Band configuration.');
      return result;
    };
    const validateSelectedProfileWeight = () => {
      if (!fitBandEnabled) return;
      const rawWeight = selectedProfile?.weightValue ?? selectedProfile?.weight_value ?? selectedProfile?.weight;
      const unit = String(selectedProfile?.weightUnit ?? selectedProfile?.weight_unit ?? '').toLowerCase();
      if (!Number.isFinite(Number(rawWeight)) || Number(rawWeight) <= 0 || !['kg', 'lb'].includes(unit)) {
        throw new Error('This product requires a weight in your fit profile. Please add your weight before continuing.');
      }
    };
    const setLineProperty = (productForm, name, value) => {
      let input = productForm.querySelector(`input[name="properties[${name}]"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        productForm.append(input);
      }
      input.value = String(value ?? '');
    };
    const selectVariantForProfile = (productForm, fitResolution = null) => {
      if (context !== 'pdp' || !productForm || (!variantMap.length && !fitResolution?.variantId)) return null;
      const size = profileSize();
      const productInfo = productForm.closest('product-info');
      const selectedColour = productInfo?.querySelector('[data-option-name="Color"]:checked, [data-option-name="Colour"]:checked, [data-option-name="颜色"]:checked')?.value || '';
      const colourIndex = Number(root.dataset.colourOptionPosition || 0) - 1;
      const sizeIndex = Number(root.dataset.sizeOptionPosition || 0) - 1;
      const fitBandIndex = Number(root.dataset.fitBandOptionPosition || 0) - 1;
      const fitBand = fitResolution?.fitBand || null;
      const candidates = variantMap.filter((variant) => {
        if (colourIndex >= 0 && selectedColour && variant.options?.[colourIndex] !== selectedColour) return false;
        if (sizeIndex >= 0 && size && normalizedSize(variant.options?.[sizeIndex]) !== normalizedSize(size)) return false;
        if (fitBandIndex >= 0 && fitBand && variant.options?.[fitBandIndex] !== fitBand) return false;
        return true;
      });
      const variant = fitResolution?.variantId
        ? {id: fitResolution.variantId, sku: fitResolution.sku || '', available: true}
        : candidates.find((item) => item.available) || candidates[0];
      if (!variant) return null;
      resolvedFitVariant = variant;
      const variantInput = productForm.querySelector('[name="id"]');
      if (!variantInput) return null;
      variantInput.disabled = false;
      variantInput.value = String(variant.id);
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      return variant;
    };
    const loadProfiles = async () => {
      const url = endpoint('profileEndpoint');
      if (!url || !email) return;
      setInlineStatus(status);
      setDrawerLoading(true, 'Loading fit profiles…');
      try {
        const payload = await requestJson(url, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ action: 'query', email, context, token, shopDomain: root.dataset.shopDomain || '' }) });
        profiles = Array.isArray(payload) ? payload : (payload.profiles || []);
        selectedProfile = profiles.find((profile) => profile.isDefault) || null;
        renderProfiles();
        setInlineStatus(status);
      } catch (error) { profiles = []; renderProfiles(); setInlineStatus(status, error.message, 'warning'); }
      finally { setDrawerLoading(false); }
    };
    const renderProfiles = () => {
      profilesNode.replaceChildren();
      if (!profiles.length) { profilesNode.innerHTML = '<p class="fizo-fit-profile__empty">No saved fit profiles found.</p>'; return; }
      profiles.forEach((profile) => {
        const item = document.createElement('div');
        item.className = 'fizo-fit-profile__profile-item';
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'fizo-fit-profile__profile';
        button.classList.toggle('is-selected', selectedProfile === profile);
        const modified = profile.updatedAt || profile.createdAt || profile.scannedAt;
        const modifiedDate = modified ? new Date(modified).toLocaleDateString('en-GB') : '';
        button.innerHTML = `<span class="fizo-fit-profile__profile-icon" aria-hidden="true"><img src="${root.dataset.profileIcon || '/cdn/shop/assets/fizo-fit-profile-shoe.svg'}" alt=""></span><span class="fizo-fit-profile__profile-name"><strong>${profile.displayName || profile.name || 'Fit Profile'}</strong></span><span class="fizo-fit-profile__profile-edit">Edit</span><span class="fizo-fit-profile__profile-radio" aria-hidden="true"></span>`;
        const modifiedNode = document.createElement('small');
        modifiedNode.className = 'fizo-fit-profile__profile-created';
        modifiedNode.textContent = modifiedDate ? `UPDATED ${modifiedDate}` : '';
        item.append(button, modifiedNode);
        profilesNode.append(item);
        button.addEventListener('click', (event) => {
          if (event.target.closest('.fizo-fit-profile__profile-edit')) { openEdit(profile); return; }
          selectedProfile = profile; root.querySelectorAll('.fizo-fit-profile__profile').forEach((node) => node.classList.remove('is-selected')); button.classList.add('is-selected'); status.textContent = '';
        });
      });
    };
    const profileValue = (value) => value === null || value === undefined ? '' : String(value);
    const renderScanResult = (result) => {
      const scanVisual = root.querySelector('[data-fizo-fit-start-scan]');
      const scanResult = root.querySelector('[data-fizo-fit-scan-result]');
      const rescanRow = root.querySelector('[data-fizo-fit-rescan-row]');
      const scanStep = scanVisual?.closest('[data-fizo-fit-step="scan"]');
      const scanTitle = root.querySelector('[data-fizo-fit-scan-title]');
      const scanSubtitle = root.querySelector('[data-fizo-fit-scan-subtitle]');
      const completeKicker = root.querySelector('[data-fizo-fit-scan-complete-kicker]');
      if (!scanVisual || !scanResult) return false;
      const data = result || {};
      const leftLength = data.originalLeftFootLength ?? data.original_left_foot_length ?? data.leftFootLength ?? data.left_foot_length;
      const leftWidth = data.originalLeftFootWidth ?? data.original_left_foot_width ?? data.leftFootWidth ?? data.left_foot_width;
      const rightLength = data.originalRightFootLength ?? data.original_right_foot_length ?? data.rightFootLength ?? data.right_foot_length;
      const rightWidth = data.originalRightFootWidth ?? data.original_right_foot_width ?? data.rightFootWidth ?? data.right_foot_width;
      const adjustedLeftLength = data.adjustedLeftFootLength ?? data.adjusted_left_foot_length ?? leftLength;
      const adjustedLeftWidth = data.adjustedLeftFootWidth ?? data.adjusted_left_foot_width ?? leftWidth;
      const adjustedRightLength = data.adjustedRightFootLength ?? data.adjusted_right_foot_length ?? rightLength;
      const adjustedRightWidth = data.adjustedRightFootWidth ?? data.adjusted_right_foot_width ?? rightWidth;
      const hasScan = Boolean(data.activeScanId || data.active_scan_id || leftLength || leftWidth || rightLength || rightWidth || adjustedLeftLength || adjustedLeftWidth || adjustedRightLength || adjustedRightWidth);
      scanStep?.classList.toggle('has-scan-result', hasScan);
      if (scanTitle) scanTitle.textContent = hasScan ? 'Your scan is complete.' : 'Get your fit, precisely.';
      if (scanSubtitle) scanSubtitle.textContent = hasScan ? 'Review your measurements before continuing.' : 'Scan your feet to help us find the right fit for you.';
      if (completeKicker) completeKicker.hidden = !hasScan;
      if (!hasScan) { scanResult.hidden = true; rescanRow?.setAttribute('hidden', ''); scanVisual.classList.remove('has-result'); return false; }
      const numberText = (value) => value === null || value === undefined || value === '' ? '—' : `${value} mm`;
      root.querySelector('[data-fizo-fit-scan-left-original-length]').textContent = numberText(leftLength);
      root.querySelector('[data-fizo-fit-scan-left-original-width]').textContent = numberText(leftWidth);
      root.querySelector('[data-fizo-fit-scan-right-original-length]').textContent = numberText(rightLength);
      root.querySelector('[data-fizo-fit-scan-right-original-width]').textContent = numberText(rightWidth);
      root.querySelector('[data-fizo-fit-scan-left-length]').value = profileValue(adjustedLeftLength);
      root.querySelector('[data-fizo-fit-scan-left-width]').value = profileValue(adjustedLeftWidth);
      root.querySelector('[data-fizo-fit-scan-right-length]').value = profileValue(adjustedRightLength);
      root.querySelector('[data-fizo-fit-scan-right-width]').value = profileValue(adjustedRightWidth);
      scanVisual.classList.add('has-result');
      scanResult.hidden = false;
      rescanRow?.removeAttribute('hidden');
      return true;
    };
    const hydrateProfile = (profile) => {
      if (!form) return;
      form.reset();
      const values = {
        gender: normalizeGender(profile.gender),
        sizeSystem: normalizeSizeSystem(profile.sizeSystem, normalizeGender(profile.gender)),
        manualSize: profile.manualSize || '',
        heightUnit: profile.heightUnit === 'cm' ? 'cm' : 'ft',
        heightFeet: profile.heightFeet,
        heightInches: profile.heightInches,
        heightCm: profile.heightCm,
        weight: profile.weightValue,
        weightUnit: profile.weightUnit === 'lb' ? 'lb' : 'kg',
        adjustedLeftFootLength: profile.adjustedLeftFootLength ?? profile.adjusted_left_foot_length,
        adjustedLeftFootWidth: profile.adjustedLeftFootWidth ?? profile.adjusted_left_foot_width,
        adjustedRightFootLength: profile.adjustedRightFootLength ?? profile.adjusted_right_foot_length,
        adjustedRightFootWidth: profile.adjustedRightFootWidth ?? profile.adjusted_right_foot_width,
        looseness: profile.looseness || '',
        feel: profile.feel || '',
        name: profile.displayName || profile.name || ''
      };
      Object.entries(values).forEach(([name, value]) => {
        form.querySelectorAll(`[name="${CSS.escape(name)}"]`).forEach((field) => {
          if (field.type === 'radio') field.checked = field.value === value;
          else field.value = profileValue(value);
        });
      });
      syncSizeUI(values.gender, values.sizeSystem, values.manualSize);
      root.querySelectorAll('[data-fizo-unit-target="height"]').forEach((button) => button.classList.toggle('is-active', button.dataset.fizoMeasurementUnit === values.heightUnit));
      root.querySelectorAll('[data-fizo-unit-target="weight"]').forEach((button) => button.classList.toggle('is-active', button.dataset.fizoMeasurementUnit === values.weightUnit));
      syncHeightUnitUI(values.heightUnit);
      root.querySelector(`[data-fizo-measurement="weight"] .fizo-fit-profile__measurement-suffix`).textContent = values.weightUnit;
      renderScanResult(profile);
    };
    const openEdit = (profile) => {
      flowMode = 'edit';
      selectedProfile = profile;
      editingProfileId = profile.id || profile.profileId || '';
      status.textContent = '';
      setNameError();
      hydrateProfile(profile);
      setStep(1);
    };
    const renderSummary = () => {
      const summary = root.querySelector('[data-fizo-fit-summary]');
      if (!summary || !form) return;
      const values = currentFormData();
      const height = values.heightUnit === 'cm'
        ? (values.heightCm || '—')
        : `${values.heightFeet || '—'}'${values.heightInches || '0'}"`;
      const weight = values.weight || '—';
      const feel = ({ SNUG: 'Snug', STANDARD: 'Standard', ROOMY: 'Roomy' })[values.looseness] || '—';
      const midsole = ({ SOFT: 'Soft', BALANCED: 'Medium', FIRM: 'Firm' })[values.feel] || '—';
      const icon = root.dataset.profileIcon || '/cdn/shop/assets/fizo-fit-profile-shoe.svg';
      const sizeSystem = ({ US_M: 'US M', US_W: 'US W', UK: 'UK', EUR: 'EUR', USA: 'US M', EU: 'EUR' })[values.sizeSystem] || 'US M';
      summary.innerHTML = `<div class="fizo-fit-profile__summary-row"><span>Size (${sizeSystem})</span><strong>${values.manualSize || '—'}</strong></div><div class="fizo-fit-profile__summary-row"><span>Height (${values.heightUnit || 'ft'})</span><strong>${height}</strong></div><div class="fizo-fit-profile__summary-row"><span>Weight (${values.weightUnit || 'kg'})</span><strong>${weight}</strong></div><div class="fizo-fit-profile__summary-row fizo-fit-profile__summary-choice"><span>Feel</span><strong><img src="${icon}" alt="">${feel}</strong></div><div class="fizo-fit-profile__summary-row fizo-fit-profile__summary-choice"><span>Midsole</span><strong><img src="${icon}" alt="">${midsole}</strong></div>`;
    };
    const openCreate = () => {
      flowMode = 'create';
      selectedProfile = null;
      editingProfileId = '';
      status.textContent = '';
      setNameError();
      setInlineStatus(sizeStatus);
      setInlineStatus(root.querySelector('[data-fizo-fit-scan-status]'));
      if (toastTimer) window.clearTimeout(toastTimer);
      root.querySelector('[data-fizo-fit-toast]')?.classList.remove('is-visible');
      const savedDraft = readDraft();
      if (savedDraft) {
        draft = savedDraft;
        form?.reset();
        hydrateDraft();
        setStep(Math.max(1, Math.min(6, Number(savedDraft.stepIndex) || 1)));
        return;
      }
      clearDraft();
      form?.reset();
      renderScanResult({});
      syncSizeUI('man', 'US_M');
      [['height', 'ft'], ['weight', 'kg']].forEach(([target, unit]) => {
        const hiddenUnit = root.querySelector(`[name="${target}Unit"]`);
        if (hiddenUnit) hiddenUnit.value = unit;
        root.querySelectorAll(`[data-fizo-unit-target="${target}"]`).forEach((button) => button.classList.toggle('is-active', button.dataset.fizoMeasurementUnit === unit));
        if (target === 'height') syncHeightUnitUI(unit);
        if (target !== 'height') root.querySelector(`[data-fizo-measurement="${target}"]`)?.querySelectorAll('.fizo-fit-profile__measurement-suffix').forEach((suffix) => { suffix.textContent = unit; });
      });
      setStep(1);
    };
    const resetToEmailGate = () => {
      root.classList.add('is-email-gate');
      root.classList.remove('is-selection', 'is-size-step', 'is-about-step', 'is-preference-step', 'is-choice-step', 'is-scan-step', 'is-name-step', 'is-ready-step');
      root.querySelector('[data-fizo-fit-email-gate]').hidden = false;
      root.querySelectorAll('[data-fizo-fit-step]').forEach((node) => node.classList.remove('is-active'));
      root.querySelector('[data-fizo-fit-back]').hidden = true;
      root.querySelector('[data-fizo-fit-next]').hidden = false;
      const viewAllButton = root.querySelector('[data-fizo-fit-view-all]');
      if (viewAllButton) viewAllButton.hidden = true;
      root.querySelector('[data-fizo-fit-create]').hidden = true;
      root.querySelector('[data-fizo-fit-save]').hidden = true;
      root.querySelector('[data-fizo-fit-add]').hidden = true;
      root.querySelector('[data-fizo-fit-rebind]').hidden = true;
    };
    const enterProfileSelection = async () => {
      flowMode = 'create';
      selectedProfile = null;
      editingProfileId = '';
      status.textContent = '';
      root.classList.remove('is-email-gate');
      root.querySelector('[data-fizo-fit-email-gate]').hidden = true;
      refreshDraftState();
      setStep(0);
      await loadProfiles();
    };

    root.querySelectorAll('[data-fizo-gender]').forEach((button) => button.addEventListener('click', () => {
      const previousGender = normalizeGender(root.querySelector('[name="gender"]').value);
      const gender = normalizeGender(button.dataset.fizoGender);
      const previousSystem = normalizeSizeSystem(root.querySelector('[name="sizeSystem"]').value, previousGender);
      const nextSystem = gender === 'woman' ? 'US_W' : 'US_M';
      const currentSize = root.querySelector('[name="manualSize"]:checked')?.value || '';
      syncSizeUI(gender, nextSystem, convertManualSize(currentSize, previousGender, previousSystem, gender, nextSystem));
      saveDraft();
    }));
    root.querySelectorAll('[data-fizo-size-system]').forEach((button) => button.addEventListener('click', () => {
      const gender = normalizeGender(root.querySelector('[name="gender"]').value);
      const previousSystem = normalizeSizeSystem(root.querySelector('[name="sizeSystem"]').value, gender);
      const system = button.dataset.fizoSizeSystem === 'US_M' || button.dataset.fizoSizeSystem === 'US_W' ? (gender === 'woman' ? 'US_W' : 'US_M') : button.dataset.fizoSizeSystem;
      const currentSize = root.querySelector('[name="manualSize"]:checked')?.value || '';
      syncSizeUI(gender, system, convertManualSize(currentSize, gender, previousSystem, gender, system));
      saveDraft();
    }));
    root.querySelectorAll('[data-fizo-measurement-unit]').forEach((button) => button.addEventListener('click', () => {
      const target = button.dataset.fizoUnitTarget;
      const hidden = root.querySelector(`[name="${target}Unit"]`);
      const previousUnit = hidden?.value || (target === 'height' ? 'ft' : 'kg');
      const nextUnit = button.dataset.fizoMeasurementUnit;
      convertMeasurementUnit(target, previousUnit, nextUnit);
      if (hidden) hidden.value = button.dataset.fizoMeasurementUnit;
      root.querySelectorAll(`[data-fizo-unit-target="${target}"]`).forEach((node) => node.classList.toggle('is-active', node === button));
      const measurement = button.closest('[data-fizo-measurement]');
      if (target === 'height') syncHeightUnitUI(button.dataset.fizoMeasurementUnit);
      if (target !== 'height') measurement?.querySelectorAll('.fizo-fit-profile__measurement-suffix').forEach((suffix) => { suffix.textContent = button.dataset.fizoMeasurementUnit; });
      saveDraft();
    }));
    root.querySelectorAll('[data-fizo-number-step]').forEach((button) => button.addEventListener('click', () => {
      const input = button.closest('.fizo-fit-profile__measurement-input')?.querySelector('input[type="number"]');
      if (!input) return;
      const step = Number(input.step) || 1;
      const current = input.value === '' ? (button.dataset.fizoNumberStep === 'up' ? Number(input.min) || 0 : Number(input.max) || 0) : Number(input.value);
      const next = current + (button.dataset.fizoNumberStep === 'up' ? step : -step);
      const min = input.min === '' ? -Infinity : Number(input.min);
      const max = input.max === '' ? Infinity : Number(input.max);
      input.value = String(Math.min(max, Math.max(min, next)));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    const setHomepageModalStyles = (open) => {
      if (!isHomepageModal || !drawer) return;
      if (open) {
        if (root.parentNode !== document.body) document.body.appendChild(root);
        Object.assign(root.style, { position: 'fixed', top: '0', left: '0', width: '100vw', height: '100vh', margin: '0', zIndex: '120', pointerEvents: 'auto' });
        Object.assign(drawer.style, { display: 'flex', position: 'fixed', top: '50vh', left: '50vw', right: 'auto', bottom: 'auto', transform: 'translate(-50%, -50%)', opacity: '1', visibility: 'visible', zIndex: '122' });
        if (backdrop) Object.assign(backdrop.style, { position: 'fixed', top: '0', left: '0', width: '100vw', height: '100vh', zIndex: '121', pointerEvents: 'none' });
      } else {
        root.removeAttribute('style');
        drawer.removeAttribute('style');
        backdrop?.removeAttribute('style');
        if (originalParent) originalParent.insertBefore(root, originalNextSibling);
      }
    };
    root.querySelectorAll('[data-fizo-fit-close]').forEach((button) => button.addEventListener('click', () => {
      saveDraft();
      if (!authenticated) {
        email = '';
        const emailInput = root.querySelector('[name="fitProfileEmail"]');
        if (emailInput) emailInput.value = '';
        resetToEmailGate();
        root.classList.remove('is-selection', 'is-size-step', 'is-about-step', 'is-preference-step', 'is-choice-step', 'is-scan-step', 'is-name-step', 'is-ready-step');
        if (toastTimer) window.clearTimeout(toastTimer);
        root.querySelector('[data-fizo-fit-toast]')?.classList.remove('is-visible');
      }
      setDrawerLoading(false); setHomepageModalStyles(false); root.classList.remove('is-open'); drawer?.setAttribute('aria-hidden', 'true'); document.documentElement.classList.remove('fizo-fit-profile-open'); document.body.classList.remove('fizo-fit-profile-open');
    }));
    form?.addEventListener('input', (event) => {
      if (event.target.name === 'manualSize' && event.target.checked) setInlineStatus(sizeStatus);
      if (event.target === nameInput && nameInput.value.trim()) setNameError();
      if (index === 6) renderSummary();
      saveDraft();
    });
    form?.addEventListener('change', () => { if (index === 6) renderSummary(); saveDraft(); });
    root.querySelector('[data-fizo-fit-create]')?.addEventListener('click', openCreate);
    root.querySelector('[data-fizo-fit-view-all]')?.addEventListener('click', async () => { await enterProfileSelection(); });
    root.querySelector('[data-fizo-fit-back]')?.addEventListener('click', () => { setStep(index - 1); saveDraft(); });
    root.querySelector('[data-fizo-fit-next]')?.addEventListener('click', () => {
      if (root.classList.contains('is-email-gate')) { const input = root.querySelector('[name="fitProfileEmail"]'); if (!input.checkValidity()) { showValidationToast(validationMessage(input)); return; } email = input.value.trim().toLowerCase(); enterProfileSelection(); return; }
      if (index === 0) { showValidationToast('Select a fit profile or create a new one.'); return; }
      const active = root.querySelector(`[data-fizo-fit-step="${steps[index]}"]`);
      const invalidField = active && [...active.querySelectorAll('[required]')].find((field) => !field.checkValidity());
      if (invalidField) {
        if (invalidField.name === 'manualSize') setInlineStatus(sizeStatus, validationMessage(invalidField), 'warning');
        else showValidationToast(validationMessage(invalidField));
        return;
      }
      if (steps[index] === 'scan') {
        const hasScan = active.classList.contains('has-scan-result');
        const skipScan = active.querySelector('[name="skip_scan"]')?.checked;
        if (!hasScan && !skipScan) { void launchScanner(); return; }
      }
      setStep(index + 1); saveDraft();
    });
    root.querySelector('[data-fizo-fit-save]')?.addEventListener('click', async () => {
      const invalidField = [...form.querySelectorAll('[required]')].find((field) => !field.checkValidity());
      if (invalidField === nameInput || !nameInput?.value.trim()) {
        setNameError(validationMessage(nameInput));
        nameInput?.focus();
        return;
      }
      if (invalidField) { showValidationToast(validationMessage(invalidField)); return; }
      setNameError();
      const url = endpoint('profileEndpoint');
      if (!url) { status.textContent = 'Profile service is not configured yet.'; return; }
      saveDraft();
      const payload = {...(flowMode === 'create' ? draft : {}), ...currentFormData(), email, context, token};
      const saveButton = root.querySelector('[data-fizo-fit-save]');
      const isCreating = !editingProfileId;
      saveButton.disabled = true;
      setInlineStatus(status);
      setDrawerLoading(true, 'Saving profile…');
      try { const result = await requestJson(url, { method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({ action: isCreating ? 'create' : 'update', profileId: editingProfileId || null, authenticated, shopifyCustomerGid: customerGid || null, shopDomain: root.dataset.shopDomain || '', ...payload }) }); selectedProfile = result.profile || result.data?.profile || result.data || result; setInlineStatus(status); if (isCreating) { clearDraft(); setStep(7); } else { await enterProfileSelection(); } } catch (error) { setInlineStatus(status, error.message, 'warning'); } finally { saveButton.disabled = false; setDrawerLoading(false); }
    });
    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-fizo-fit-ready-edit]')) {
        flowMode = 'edit';
        editingProfileId = selectedProfile?.id || selectedProfile?.profileId || '';
        setStep(6);
      }
    });
    launchScanner = async () => {
      if (root.querySelector('.fizo-fit-profile__volumental-launcher')) return;
      const url = endpoint('scanSessionEndpoint');
      const scanStatus = root.querySelector('[data-fizo-fit-scan-status]');
      setInlineStatus(scanStatus);
      let widgetHost;
      const cleanupWidget = (delay = 0) => {
        const host = widgetHost;
        if (!host) return;
        window.setTimeout(() => host.remove(), delay);
      };
      const scannerLoading = createScannerLaunchLoading({
        setLoading: setDrawerLoading,
        setStatus: (message, tone) => setInlineStatus(scanStatus, message, tone),
        onTimeout: cleanupWidget,
      });
      try {
        scannerLoading.start();
        await loadVolumentalSdk(root);
        if (!scannerLoading.isPending()) return;
        if (!window.customElements?.get('volumental-measurement')) {
          await Promise.race([
            customElements.whenDefined('volumental-measurement'),
            new Promise((_, reject) => window.setTimeout(() => reject(new Error('The Volumental scanner could not be loaded.')), 10000)),
          ]);
        }
        if (!scannerLoading.isPending()) return;
        const widget = document.createElement('volumental-measurement');
        widget.setAttribute('locale', document.documentElement.lang || 'en');
        widget.setAttribute('measurement-on-widget', 'true');
        widget.setAttribute('theme', 'light');
        widget.setAttribute('variant', 'button');
        const scanVisual = root.querySelector('[data-fizo-fit-start-scan]');
        widgetHost = document.createElement('div');
        widgetHost.className = 'fizo-fit-profile__volumental-launcher';
        widgetHost.style.cssText = 'position:absolute;inset:0;z-index:6;opacity:0;';
        widgetHost.addEventListener('click', (event) => event.stopPropagation());
        widget.style.cssText = 'display:block;width:100%;height:100%;';
        widgetHost.append(widget);
        scanVisual?.append(widgetHost);
        let measurementReceived = false;
        widget.addEventListener('volumental:on-opened', () => { scannerLoading.opened(); });
        widget.addEventListener('volumental:on-closed', () => {
          scannerLoading.finish();
          if (!measurementReceived) setInlineStatus(scanStatus, 'No completed new scan was received. Please complete the full scan and save it before closing.', 'warning');
          cleanupWidget();
        });
        widget.addEventListener('volumental:on-error', () => {
          scannerLoading.finish();
          setInlineStatus(scanStatus, 'The scan could not be completed. Please try again.', 'warning');
          cleanupWidget();
        });
        widget.addEventListener('volumental:on-measurement', async (event) => {
          const scanId = event?.detail?.id;
          if (!scanId || handledScanIds.has(scanId)) return;
          measurementReceived = true;
          handledScanIds.add(scanId);
          setInlineStatus(scanStatus, 'Reading your fit profile...');
          setDrawerLoading(true, 'Loading scan results…');
          try {
            if (!url) throw new Error('Scan service is not configured yet.');
            const scanSession = await requestJson(url, { method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({ profileId: null, context, token, email, shopDomain: root.dataset.shopDomain || '', returnUrl: location.href }) });
            const scanSessionId = typeof scanSession === 'string' ? scanSession : (scanSession.scanSession || scanSession.sessionId || scanSession.id);
            if (!scanSessionId) throw new Error('Scan session could not be created.');
            const completion = await requestJson(endpoint('scanResultsEndpoint'), { method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({ scanSession: scanSessionId, scanId }) });
            let resultData = completion.data || completion.profile || completion;
            if (resultData.alreadyImported && !resultData.leftFootLength && !resultData.originalLeftFootLength) {
              const profilePayload = await requestJson(endpoint('profileEndpoint'), { method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({ action: 'query', email, context, token, shopDomain: root.dataset.shopDomain || '' }) });
              const existingProfiles = Array.isArray(profilePayload) ? profilePayload : (profilePayload.profiles || []);
              resultData = existingProfiles.find((profile) => (profile.activeScanId || profile.active_scan_id) === scanId) || resultData;
            }
            editingProfileId = resultData.profileId || resultData.id || editingProfileId;
            if (editingProfileId) flowMode = 'edit';
            const recommended = resultData.recommendedSizeText || resultData.recommended_size_text || '';
            const recommendedParts = recommended.match(/Left\s+(.+?)\s*\/\s*Right\s+(.+)/i);
            const leftSize = resultData.leftSize || resultData.left_size || recommendedParts?.[1] || '—';
            const rightSize = resultData.rightSize || resultData.right_size || recommendedParts?.[2] || '—';
            if (!renderScanResult({...resultData, leftSize, rightSize})) throw new Error('The scan was saved, but its measurements could not be loaded.');
            saveDraft({
              scanSession,
              scanId,
              originalLeftFootLength: resultData.originalLeftFootLength ?? resultData.original_left_foot_length ?? resultData.leftFootLength ?? resultData.left_foot_length,
              originalLeftFootWidth: resultData.originalLeftFootWidth ?? resultData.original_left_foot_width ?? resultData.leftFootWidth ?? resultData.left_foot_width,
              originalRightFootLength: resultData.originalRightFootLength ?? resultData.original_right_foot_length ?? resultData.rightFootLength ?? resultData.right_foot_length,
              originalRightFootWidth: resultData.originalRightFootWidth ?? resultData.original_right_foot_width ?? resultData.rightFootWidth ?? resultData.right_foot_width,
            });
            setInlineStatus(scanStatus, 'Scan complete.', 'success');
          } catch (error) { handledScanIds.delete(scanId); setInlineStatus(scanStatus, error instanceof Error ? error.message : 'We could not read this scan. Please try again.', 'warning'); }
          finally { setDrawerLoading(false); cleanupWidget(); }
        });
        const launchButton = widget.shadowRoot?.querySelector('button') || widget;
        launchButton.click();
      } catch (error) {
        cleanupWidget();
        scannerLoading.failed(error instanceof Error ? error.message : 'Scan could not be started.');
      }
    };
    root.querySelector('[data-fizo-fit-rescan]')?.addEventListener('click', () => { void launchScanner(); });
    if (context === 'pdp') root.querySelector('[data-fizo-fit-add]')?.addEventListener('click', async () => {
      if (!selectedProfile) { showValidationToast('Select a fit profile before adding to bag.'); return; }
      const addButton = root.querySelector('[data-fizo-fit-add]');
      const submit = document.querySelector('product-info form [type="submit"]:not([data-fizo-open-fit-profile])');
      if (!addButton || !submit?.form || addButton.disabled) return;

      const finish = (successful, message = '') => {
        unsubscribeUpdate?.();
        unsubscribeError?.();
        addButton.disabled = false;
        addButton.removeAttribute('aria-busy');
        addButton.classList.remove('is-loading');
        setDrawerLoading(false);
        if (successful) root.querySelector('[data-fizo-fit-close]')?.click();
        else showValidationToast(message || 'Unable to add this item to your bag. Please try again.');
      };
      const unsubscribeUpdate = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
        ? subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event?.source === 'product-form') finish(true);
          })
        : null;
      const unsubscribeError = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
        ? subscribe(PUB_SUB_EVENTS.cartError, (event) => {
            if (event?.source === 'product-form') finish(false, event?.errors || event?.message);
          })
        : null;

      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      addButton.classList.add('is-loading');
      setDrawerLoading(true, 'Adding to bag…');
      try {
        validateSelectedProfileWeight();
        const fitResolution = await resolveFitBandForProfile(submit.form);
        const selectedVariant = selectVariantForProfile(submit.form, fitResolution);
        if (!selectedVariant) throw new Error('A compatible SKU could not be found for this fit profile.');
        const productInfo = submit.form.closest('product-info');
        setLineProperty(submit.form, '_fizo_experiment', productInfo?.dataset.fizoExperimentKey || 'PDP_FIT_PROFILE_V1');
        setLineProperty(submit.form, '_fizo_experiment_group', 'A');
        setLineProperty(submit.form, '_fizo_experiment_assignment_id', productInfo?.dataset.fizoExperimentAssignmentId || '');
        setLineProperty(submit.form, '_fizo_profile_id', selectedProfile?.id || selectedProfile?.profileId || '');
      } catch (error) {
        finish(false, error instanceof Error ? error.message : 'Unable to resolve a compatible SKU.');
        return;
      }
      try {
        await recordPdpUsage(submit.form);
      } catch (error) {
        console.warn('Fit profile usage event could not be recorded', error);
      }
      submit.form.requestSubmit(submit);
    });
    root.querySelector('[data-fizo-fit-rebind]')?.addEventListener('click', () => {
      if (!selectedProfile) { showValidationToast('Select a fit profile before continuing.'); return; }
      try { validateSelectedProfileWeight(); } catch (error) { showValidationToast(error.message); return; }
      document.dispatchEvent(new CustomEvent('fizo:order-profile-confirmed', { detail: { profile: selectedProfile } }));
      root.querySelector('[data-fizo-fit-close]')?.click();
    });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-fizo-open-fit-profile]');
      if (!trigger) return;
      const triggerContext = trigger.dataset.fizoFitContext || '';
      if (triggerContext && triggerContext !== context) return;
      event.preventDefault();
      root.classList.add('is-open');
      setHomepageModalStyles(true);
      drawer?.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('fizo-fit-profile-open');
      document.body.classList.add('fizo-fit-profile-open');
      if (authenticated) enterProfileSelection();
      else resetToEmailGate();
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && root.classList.contains('is-open')) root.querySelector('[data-fizo-fit-close]')?.click(); });
  });
})();
